import React, { useState, useEffect } from "react";
import {
  Box,
  CloseButton,
  HStack,
  Icon,
  Link,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FiBookOpen, FiGlobe, FiFileText } from "react-icons/fi";
import { useTranslation } from "react-i18next";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://api.globalwineinstitute.org";
const API_KEY =
  process.env.REACT_APP_API_KEY || "f3bb4afa55070cc052d1b888d4f7465a";

function dismissKey(year, week) {
  return `weekly_summary_dismissed_${year}_W${week}`;
}

function isDismissed(year, week) {
  try {
    return sessionStorage.getItem(dismissKey(year, week)) === "1";
  } catch {
    return false;
  }
}

function dismiss(year, week) {
  try {
    sessionStorage.setItem(dismissKey(year, week), "1");
  } catch {}
}

function RefList({ refs }) {
  if (!refs || refs.length === 0) return null;
  return (
    <VStack align="stretch" spacing={1} mt={3} pt={2} borderTop="1px solid" borderColor="border.default">
      <Text fontSize="10px" fontWeight="600" color="text.muted" textTransform="uppercase" letterSpacing="wider">
        Sources
      </Text>
      {refs.map((r, i) => (
        <HStack key={i} spacing={1} align="baseline">
          <Text fontSize="11px" color="gray.400">-</Text>
          {r.url ? (
            <Link href={r.url} isExternal fontSize="11px" color="gwbi.600" _hover={{ textDecor: "underline", color: "gwbi.700" }}>
              {r.title || r.source}
            </Link>
          ) : (
            <Text fontSize="11px" color="gray.600">{r.title || r.source}</Text>
          )}
          {r.source && r.url && (
            <Text fontSize="10px" color="gray.500">({r.source})</Text>
          )}
        </HStack>
      ))}
    </VStack>
  );
}

function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let paraLines = [];

  const flushPara = () => {
    if (paraLines.length === 0) return;
    const raw = paraLines.join(" ");
    paraLines = [];
    const parts = raw.split(/(\*\*.*?\*\*|\*.*?\*)/g).map((seg, j) => {
      if (seg.startsWith("**") && seg.endsWith("**"))
        return <Text as="span" key={j} fontWeight="700" color="gray.900">{seg.slice(2, -2)}</Text>;
      if (seg.startsWith("*") && seg.endsWith("*"))
        return <Text as="span" key={j} fontStyle="italic">{seg.slice(1, -1)}</Text>;
      return seg;
    });
    elements.push(
      <Text key={elements.length} fontSize="sm" color="gray.700" lineHeight="1.85" mb={3}>
        {parts}
      </Text>
    );
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (/^#{1,3}\s+/.test(trimmed)) {
      flushPara();
      const title = trimmed.replace(/^#{1,3}\s+/, "");
      elements.push(
        <Text
          key={elements.length}
          fontSize="md"
          fontWeight="800"
          color="gwbi.700"
          mt={elements.length > 0 ? 4 : 0}
          mb={2}
          letterSpacing="0.01em"
        >
          {title}
        </Text>
      );
    } else if (trimmed === "") {
      flushPara();
    } else {
      paraLines.push(trimmed);
    }
  }
  flushPara();
  return elements;
}

function SectionContent({ text, refs }) {
  return (
    <Box>
      {renderMarkdown(text)}
      <RefList refs={refs} />
    </Box>
  );
}

export function WeeklySummaryCard() {
  const { t, i18n } = useTranslation("common");
  const [data, setData] = useState(null);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    // Clean up old localStorage dismiss keys from previous version
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith("weekly_summary_dismissed_")) localStorage.removeItem(k);
      });
    } catch {}

    const lang = (i18n.language || "en").slice(0, 2);
    fetch(`${API_BASE}/api/weekly-summary/latest?lang=${lang}`, {
      headers: { "X-API-KEY": API_KEY },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (d && d.year && d.week) {
          if (isDismissed(d.year, d.week)) {
            setMinimized(true);
          }
          setData(d);
        }
      })
      .catch(() => {});
  }, [i18n.language]);

  if (!data) return null;

  const handleDismiss = () => {
    dismiss(data.year, data.week);
    setMinimized(true);
  };

  const handleReopen = () => {
    try { sessionStorage.removeItem(dismissKey(data.year, data.week)); } catch {}
    setMinimized(false);
  };

  if (minimized) {
    return (
      <Box
        borderRadius="ui-lg"
        overflow="hidden"
        mb={4}
        bg="surface.card"
        borderWidth="1px"
        borderColor="border.default"
        boxShadow="ui-sm"
        px={4}
        py={3}
        cursor="pointer"
        onClick={handleReopen}
        _hover={{ bg: "surface.subtle" }}
        transition="background 0.15s ease"
      >
        <HStack spacing={2} align="center">
          <Icon as={FiBookOpen} color="gwbi.600" boxSize={4} />
          <Text fontSize="sm" fontWeight="600" color="gray.900">
            {t("weekly_summary.title", "This Week in Wine")}
          </Text>
          <Text fontSize="xs" color="text.muted">
            — {t("weekly_summary.click_to_reopen", "click to read")}
          </Text>
        </HStack>
      </Box>
    );
  }

  return (
    <Box
      borderRadius="ui-lg"
      overflow="hidden"
      bg="surface.card"
      borderWidth="1px"
      borderColor="border.default"
      boxShadow="ui-sm"
      mb={4}
      position="relative"
    >
      {/* Banner header with full image */}
      <Box
        position="relative"
        h="100px"
        bgImage="url('https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=800&q=80')"
        bgSize="cover"
        bgPosition="center 40%"
      >
        <Box
          position="absolute"
          inset={0}
          bg="linear-gradient(to top, rgba(58, 23, 20, 0.92) 0%, rgba(58, 23, 20, 0.35) 100%)"
        />
        <CloseButton
          size="sm"
          position="absolute"
          top={2}
          right={2}
          zIndex={2}
          color="whiteAlpha.800"
          _hover={{ color: "white", bg: "whiteAlpha.300" }}
          onClick={handleDismiss}
          aria-label={t("weekly_summary.dismiss", "Dismiss until next week")}
        />
        <VStack
          position="relative"
          zIndex={1}
          h="100%"
          justify="center"
          align="start"
          px={5}
          spacing={0}
        >
          <Text
            fontSize="10px"
            fontWeight="700"
            color="whiteAlpha.800"
            textTransform="uppercase"
            letterSpacing="0.15em"
          >
            {t("weekly_summary.week_label", "Week {{week}}, {{year}}", {
              week: data.week,
              year: data.year,
            })}
          </Text>
          <Text
            fontFamily="heading"
            fontSize="xl"
            fontWeight="bold"
            color="white"
            lineHeight="1.2"
            mt={1}
            sx={{ textShadow: "0 1px 6px rgba(0,0,0,0.45)" }}
          >
            {t("weekly_summary.title", "This Week in Wine")}
          </Text>
        </VStack>
      </Box>

      {/* Content body — light, with bordeaux accents */}
      <Box bg="surface.card" p={4} pt={3}>
        <Tabs size="sm" variant="soft-rounded" colorScheme="gwbi">
          <TabList gap={2} mb={3}>
            {[
              { icon: FiFileText, label: t("weekly_summary.journal_tab", "Business") },
              { icon: FiBookOpen, label: t("weekly_summary.academic_tab", "Academic") },
              { icon: FiGlobe, label: t("weekly_summary.policy_tab", "Policy") },
            ].map((tab) => (
              <Tab
                key={tab.label}
                fontSize="xs"
                px={4}
                py={1.5}
                fontWeight="600"
                color="gray.600"
                bg="surface.subtle"
                borderWidth="1px"
                borderColor="border.default"
                _selected={{ color: "white", bg: "gwbi.600", borderColor: "gwbi.600" }}
                _hover={{ color: "gray.800", bg: "surface.muted" }}
              >
                <Icon as={tab.icon} mr={1.5} boxSize={3} />
                {tab.label}
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            <TabPanel px={0} py={2}>
              <SectionContent text={data.journal_news} refs={data.journal_refs} />
            </TabPanel>
            <TabPanel px={0} py={2}>
              <SectionContent text={data.academic_news} refs={data.academic_refs} />
            </TabPanel>
            <TabPanel px={0} py={2}>
              <SectionContent text={data.policy_news} refs={data.policy_refs} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
}
